import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileToBase64, arabicError } from "../lib/api";
import { toast } from "sonner";
import { Upload as UploadIcon, Image as ImageIcon, DollarSign, Ruler, Crop, Maximize2 } from "lucide-react";
import { PRODUCT_SECTORS, CATEGORIES_BY_SECTOR, PRODUCT_COLORS, COUNTRIES, UNITS, unitOf, badCountQty, INT_QTY_MSG, qtyInputProps } from "../lib/constants";
import QuadCropperModal from "../components/QuadCropperModal";
import SearchableSelect from "../components/SearchableSelect";
import SelectWithOther from "../components/SelectWithOther";
import { useAuth } from "../context/AuthContext";

const UploadPhoto = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEmployee = user?.role === "employee";
  const emptyForm = {
    category: CATEGORIES_BY_SECTOR[PRODUCT_SECTORS[0]][0], type: PRODUCT_SECTORS[0], country: "سوريا",
    size: "", color: "", manufacturer: "", price_usd: "", quantity_meters: "", cost_usd: "", product_code: "", unit: "meter",
  };
  const [form, setForm] = useState(emptyForm);
  const [branches, setBranches] = useState([]);      // owner: own branches (default Main); employee: all branches of the same store
  const [branchId, setBranchId] = useState("");
  const [myBranch, setMyBranch] = useState(null);    // employee / branch manager: assigned branch (preselected)
  useEffect(() => {
    if (isEmployee) api.get("/employee/me").then((r) => { setMyBranch(r.data.branch); setBranches(r.data.branches || []); setBranchId(r.data.branch?.id || ""); }).catch(() => {});
    else api.get("/branches").then((r) => { setBranches(r.data); setBranchId(r.data.find((b) => b.is_main)?.id || ""); }).catch(() => {});
  }, [isEmployee]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rawImage, setRawImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [mode, setMode] = useState("original"); // "original" or "cropped"
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  // Phase 5A: categories follow the chosen sector; a custom ("أخرى") sector offers only a custom category.
  const categoryOptions = CATEGORIES_BY_SECTOR[form.type] || [];
  const unit = unitOf(form.unit);                                            // Phase 5B labels only; numbers unchanged
  const sectorKey = CATEGORIES_BY_SECTOR[form.type] ? form.type : "__custom__";   // remounts the category select only when the sector bucket changes
  const onSectorChange = (v) => setForm((s) => {
    const sameBucket = (CATEGORIES_BY_SECTOR[v] ? v : "__custom__") === (CATEGORIES_BY_SECTOR[s.type] ? s.type : "__custom__");
    return { ...s, type: v, category: sameBucket ? s.category : (CATEGORIES_BY_SECTOR[v]?.[0] || "") };   // reset ONLY the unsaved category
  });

  const autoName = `${form.type}_${form.category}_${form.country}_${form.size || "?"}.jpg`;

  const onFile = async (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(f.type)) {
      toast.error("يجب أن تكون الصورة بصيغة JPG أو PNG"); return;
    }
    if (f.size > 10 * 1024 * 1024) { toast.error("حجم الصورة أكبر من 10MB"); return; }
    setFile(f);
    const raw = await fileToBase64(f);
    setRawImage(raw);
    setPreview(raw);
    setMode("original");
  };

  const onCropConfirm = (dataUrl) => {
    setPreview(dataUrl);
    setMode("cropped");
    setShowCropper(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!preview) { toast.error("الرجاء اختيار صورة"); return; }
    if (!form.size || !form.price_usd || !form.quantity_meters || !form.type.trim() || !form.category.trim()) {
      toast.error("يرجى ملء الحقول المطلوبة"); return;
    }
    const price = parseFloat(form.price_usd);
    const meters = parseFloat(form.quantity_meters);
    if (price < 0 || !isFinite(price)) { toast.error("سعر غير صالح"); return; }
    if (meters <= 0 || !isFinite(meters)) { toast.error("كمية غير صالحة"); return; }
    if (badCountQty(form.unit, meters)) { toast.error(INT_QTY_MSG); return; }
    setLoading(true);
    try {
      const payload = {
        category: form.category, type: form.type, country: form.country,
        size: form.size, color: form.color, manufacturer: form.manufacturer,
        price_usd: price, quantity_meters: meters, image_base64: preview,
        product_code: form.product_code.trim() || null,
        unit: form.unit,
      };
      if (branchId) payload.branch_id = branchId;
      if (!isEmployee) { payload.cost_usd = form.cost_usd === "" ? null : parseFloat(form.cost_usd); }
      await api.post("/photos", payload);
      toast.success("تم رفع الصورة");
      if (isEmployee) {
        setForm(emptyForm); setFile(null); setPreview(null); setRawImage(null); setMode("original");
      } else {
        navigate("/my-photos");
      }
    } catch (err) { toast.error(arabicError(err)); } finally { setLoading(false); }
  };

  const inputCls = "h-12 w-full rounded-lg border border-[#7AB2B2] bg-white px-4 text-[#333333] focus:outline-none focus:ring-2 focus:ring-[#09637E]";

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="upload-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">رفع صورة جديدة</h1>
        <p className="text-[#666666] mt-1">أضف منتجاً بالدولار الأمريكي مع الكمية حسب نوع المقاس</p>
      </div>

      <form onSubmit={onSubmit} className="bg-white border border-[#7AB2B2] rounded-2xl p-6 space-y-5" data-testid="upload-form">
        <div className="bg-[#F5FAFB] border border-[#DCEBEE] rounded-lg p-3" data-testid="upload-branch-box">
          <label className="block text-sm font-bold text-[#333333] mb-1">الفرع الذي يُرفع إليه المنتج</label>
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={inputCls} data-testid="upload-branch">
            {branches.length === 0 && <option value="">{isEmployee ? (myBranch?.name || "الفرع الرئيسي") : "الفرع الرئيسي"}</option>}
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {isEmployee && myBranch?.name && <div className="text-xs text-[#666666] mt-1" data-testid="upload-branch-assigned">الفرع المعيّن لحسابك: {myBranch.name}</div>}
        </div>
        <div>
          <label className="block text-sm font-bold text-[#333333] mb-2">صورة المنتج *</label>

          <label htmlFor="file-input" className="block cursor-pointer">
            <div className="border-2 border-dashed border-[#7AB2B2] rounded-xl p-6 hover:border-[#09637E] hover:bg-[#EBF4F6] transition-colors">
              {preview ? (
                <div className="flex items-center gap-4">
                  <div className="w-32 h-32 rounded-lg overflow-hidden bg-[#EBF4F6] flex items-center justify-center shrink-0">
                    <img
                      src={preview}
                      alt=""
                      className="w-full h-full object-contain"
                      data-testid="upload-preview"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#333333] truncate">{file?.name}</div>
                    <div className="text-sm text-[#09637E]">سيُحفظ باسم: {autoName}</div>
                    <div className="text-xs text-[#666666] mt-1" data-testid="upload-image-mode">
                      {mode === "cropped" ? "الصورة مقصوصة" : "الصورة الأصلية بدون قصّ"}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button type="button" onClick={(e) => { e.preventDefault(); setShowCropper(true); }} data-testid="crop-image-btn"
                        className="h-9 px-3 rounded-lg border-2 border-[#088395] text-[#088395] font-bold text-xs flex items-center gap-1 hover:bg-[#EBF4F6]">
                        <Crop size={14} /> {mode === "cropped" ? "إعادة القصّ" : "قصّ وتعديل (اختياري)"}
                      </button>
                      {mode === "cropped" && (
                        <button type="button" onClick={(e) => { e.preventDefault(); setPreview(rawImage); setMode("original"); }} data-testid="use-original-btn"
                          className="h-9 px-3 rounded-lg border-2 border-[#7AB2B2] text-[#666666] font-bold text-xs flex items-center gap-1 hover:bg-[#EBF4F6]">
                          <Maximize2 size={14} /> استخدام الأصلية
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center py-8">
                  <ImageIcon className="text-[#7AB2B2] mb-3" size={48} />
                  <div className="font-bold text-[#333333]">اختر صورة من جهازك</div>
                  <div className="text-xs text-[#666666] mt-1">يمكنك قصّها بدقة بعد الاختيار (اختياري)</div>
                </div>
              )}
            </div>
          </label>
          <input id="file-input" type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="upload-file-input" />
          <input id="camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="upload-camera-input" />
          <div className="grid grid-cols-2 gap-2 mt-2 md:hidden">
            <button type="button" onClick={() => document.getElementById("camera-input")?.click()} data-testid="capture-photo-btn"
              className="h-11 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-sm font-bold">
              التقاط صورة
            </button>
            <button type="button" onClick={() => document.getElementById("file-input")?.click()} data-testid="choose-photo-btn"
              className="h-11 rounded-lg border-2 border-[#09637E] text-[#09637E] text-sm font-bold hover:bg-[#EBF4F6]">
              اختيار صورة من جهازك
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1">نوع المنتج *</label>
            <SelectWithOther value={form.type} onChange={onSectorChange} options={PRODUCT_SECTORS}
              className={inputCls} testid="upload-type" placeholder="اكتب نوع المنتج..." />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1">الصنف *</label>
            <SelectWithOther key={sectorKey} value={form.category} onChange={(v) => set("category", v)} options={categoryOptions}
              className={inputCls} testid="upload-category" placeholder="اكتب الصنف..." />
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
            <div className="min-w-0">
              <label className="block text-sm font-bold text-[#333333] mb-1">بلد المنشأ *</label>
              <SearchableSelect value={form.country} onChange={(v) => set("country", v)} options={COUNTRIES}
                placeholder="ابحث عن دولة المنشأ..." testid="upload-country" className={inputCls} />
            </div>
            <div className="w-32 sm:w-36">
              <label className="block text-sm font-bold text-[#333333] mb-1">نوع المقاس *</label>
              <select className={inputCls} value={form.unit} onChange={(e) => set("unit", e.target.value)} data-testid="upload-unit">
                {UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1" data-testid="upload-size-label">{unit.sizeLabel} *</label>
            <input className={inputCls} placeholder={unit.sizePlaceholder} value={form.size} onChange={(e) => set("size", e.target.value)} data-testid="upload-size" required />
            {form.unit === "shoe_size" && <p className="text-[11px] text-[#777777] mt-1" data-testid="upload-shoe-hint">النمرة هنا (مثل 42)، والكمية أدناه = عدد القطع</p>}
          </div>
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1 flex items-center gap-1" data-testid="upload-price-label"><DollarSign size={14} /> {unit.priceLabel} *</label>
            <input type="number" step="0.01" min="0" className={inputCls} placeholder="12.50" value={form.price_usd}
              onChange={(e) => set("price_usd", e.target.value)} data-testid="upload-price" required />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1 flex items-center gap-1" data-testid="upload-quantity-label"><Ruler size={14} /> {unit.qtyLabel} *</label>
            <input type="number" step="0.1" min="0.1" {...qtyInputProps(form.unit)} className={inputCls} placeholder="100" value={form.quantity_meters}
              onChange={(e) => set("quantity_meters", e.target.value)} data-testid="upload-quantity" required />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-[#333333] mb-1">كود المنتج (اختياري)</label>
            <input className={inputCls} dir="ltr" maxLength={60} placeholder="مثال: ABC-123" value={form.product_code}
              onChange={(e) => set("product_code", e.target.value)} data-testid="upload-product-code" />
            <p className="text-[11px] text-[#777777] mt-1" data-testid="upload-product-code-hint">اتركه فارغاً ليتم إنشاء الكود تلقائياً</p>
          </div>
          {!isEmployee && (
          <div className="sm:col-span-2 bg-[#F5FAFB] border border-[#DCEBEE] rounded-lg p-3">
            <label className="block text-sm font-bold text-[#333333] mb-1" data-testid="upload-cost-label">رأس المال (تكلفة {form.unit === "meter" ? "المتر" : form.unit === "kg" ? "الكيلوغرام" : form.unit === "liter" ? "اللتر" : "القطعة"} $) — اختياري، خاص بك فقط</label>
            <input type="number" step="0.01" min="0" className={inputCls} placeholder="مثال: 8.50 — يُستخدم لحساب الربح في إحصائياتي" value={form.cost_usd}
              onChange={(e) => set("cost_usd", e.target.value)} data-testid="upload-cost" />
            <p className="text-[11px] text-[#777777] mt-1">لا يظهر للموظفين ولا للتجار الآخرين ولا في صفحة المتجر العامة.</p>
          </div>
          )}
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1">اللون (اختياري)</label>
            <select className={inputCls} value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="upload-color">
              <option value="">— لم يُحدَّد —</option>
              {PRODUCT_COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1">الشركة المصنعة (اختياري)</label>
            <input className={inputCls} placeholder="RAK" value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} data-testid="upload-manufacturer" />
          </div>
        </div>

        <button type="submit" disabled={loading} data-testid="upload-submit"
          className="h-12 w-full md:w-auto px-8 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold flex items-center justify-center gap-2 disabled:opacity-60">
          <UploadIcon size={18} /> {loading ? "جارٍ الرفع..." : "رفع"}
        </button>
      </form>

      {showCropper && rawImage && (
        <QuadCropperModal
          imageSrc={rawImage}
          onCancel={() => setShowCropper(false)}
          onConfirm={onCropConfirm}
        />
      )}
    </div>
  );
};

export default UploadPhoto;
